import React, { useState, useEffect } from "react";
import { Button, Input, Select, Space, Form, Checkbox } from "antd";
import { DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import {
  DeleteFilled,
  PlusOutlined,
} from '@ant-design/icons';
import { use } from "echarts";

const { TextArea } = Input;

type ConditionItem = {
  id: string;
  field?: string;
};

type Group = {
  id: string;
  type: "AND" | "OR";
  items: ConditionItem[];
};

// helper to create id
const newid = (prefix = "") => `${prefix}${Date.now()}${Math.random().toString(36).slice(2, 7)}`;

export default function MergeConditionBuilder({ stData = {} as any, stChange = (data = {}, type = null) => { } }): JSX.Element {
  const [groups, setGroups] = useState<Group[]>([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  // sample options (you will load from API instead)
  const options = [
    { value: "cus_name", label: "Tên khách hàng / tổ chức" },
    { value: "cus_birth_day", label: "Ngày sinh" },
    { value: "cus_tax_reg_no_dms", label: "Mã số thuế" },
    { value: "cus_tel", label: "SĐT" },
    { value: "cus_address", label: "Địa chỉ" },
    { value: "cus_dms_id_card_no", label: "CCCD/CMND" },

    { value: "vehicle_chassis_no_owned", label: "Số khung xe sở hữu" },
    { value: "plate_number_owned", label: "Biển số xe sở hữu" },
    { value: "vehicle_chassis_no_used", label: "Số khung xe sử dụng" },
    { value: "plate_number_used", label: "Biển số xe sử dụng" },
    // { value: "numer_plate", label: "Biển số" },
    // { value: "vehicle_chassis_no", label: "Số khung" },

  ];

  useEffect(() => {
    stChange({ groups: groups })
  }, [groups]);

  useEffect(() => {
    if (stData.clear !== 0) {
      setGroups(stData.groups || []);
    }
  }, [stData.clear]);

  const hasType = (t: "AND" | "OR") => groups.some((g) => g.type === t);

  const addGroup = (t: "AND" | "OR") => {
    if (hasType(t)) return;
    const g: Group = { id: newid("g_"), type: t, items: [] };
    // push to end to preserve order of clicks; first clicked will be at index 0 (rendered above buttons)
    setGroups((prev) => [...prev, g]);
  };

  const addCondition = (groupId: string) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, items: [...g.items, { id: newid("i_") }] } : g))
    );
  };

  const removeCondition = (groupId: string, itemId: string) => {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, items: g.items.filter((it) => it.id !== itemId) } : g)));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    // source.droppableId and destination.droppableId are group ids
    if (source.droppableId === destination.droppableId) {
      setGroups((prev) =>
        prev.map((g) => {
          if (g.id !== source.droppableId) return g;
          const items = Array.from(g.items);
          const [moved] = items.splice(source.index, 1);
          items.splice(destination.index, 0, moved);
          return { ...g, items };
        })
      );
    }
  };

  // render one group card
  const GroupCard = ({ stData = {} as any, stChange = (data = {}, type = null) => { }, group = {} as any }) => {
    const isAnd = group.type === "AND";
    const pillClass = isAnd ? "pill pill-and" : "pill pill-or";

    return (
      <div id="dynamic_form_add_seclect">
        <div className="mb-8px" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={pillClass}>{group.type}</span>
            <span className="fz-13">{`Nhóm ${group.type}`}</span>
          </div>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={group.id}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {group.items.map((it: any, idx: any) => (
                  <Draggable key={it.id} draggableId={it.id} index={idx}>
                    {(prov, snapshot) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        className="one-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          background: snapshot.isDragging ? "#fafafa" : "#ffffff",
                          ...prov.draggableProps.style,
                        }}
                      >
                        <div className="icon-drag" {...prov.dragHandleProps} style={{ cursor: "grab" }}>
                          <HolderOutlined />
                        </div>

                        <div style={{ flex: 1 }}>
                          <Select
                            className='rs-select rs-select--neutral-gray-100'
                            placeholder="Chọn điều kiện"
                            style={{ width: "100%" }}
                            value={it.field}
                            onChange={(val) => {
                              setGroups((prev) =>
                                prev.map((g) =>
                                  g.id === group.id
                                    ? { ...g, items: g.items.map((x) => (x.id === it.id ? { ...x, field: val } : x)) }
                                    : g
                                )
                              );
                            }}
                          >
                            {options
                              .filter((o) => !group.items.some((item: any) => item.id !== it.id && item.field === o.value))
                              .map((o) => (
                                <Select.Option key={o.value} value={o.value}>
                                  {o.label}
                                </Select.Option>
                              ))}
                          </Select>
                          <div>
                            <Checkbox
                              checked={it.is_unknow === 1 ? true : false}
                              className="rs-checkbox v2"
                              onChange={(e) => {
                                const isUnknow = e.target.checked ? 1 : 0;
                                setGroups((prev) =>
                                  prev.map((g) =>
                                    g.id === group.id
                                      ? { ...g, items: g.items.map((x) => (x.id === it.id ? { ...x, is_unknow: isUnknow } : x)) }
                                      : g
                                  )
                                );
                              }}
                            >
                              Bỏ qua giá trị không xác định
                            </Checkbox>
                          </div>
                        </div>
                        <div className="ant-space" style={{ marginBottom: 0 }}>
                          <DeleteFilled className='icon-delete' onClick={() => removeCondition(group.id, it.id)} />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div>
          <Button className='btn btn-primary-none-bg' icon={<PlusOutlined />} onClick={() => addCondition(group.id)}>
            Thêm điều kiện
          </Button>
        </div>
      </div>
    );
  };

  // helpers to get group by type
  const getGroupByType = (t: "AND" | "OR") => groups.find((g) => g.type === t);

  return (
    <div className="content-filter-setting">
      <div>
        <div>
          <div>
            <div
              className="form-block d-none-rq form-benefit"
            >
              <label className="ant-form-item-required" >Tên điều kiện:</label>
              <TextArea rows={2} value={stData.name} onChange={(e) => stChange({ name: e.target.value })} placeholder="(Bản ghi A.SDT = Bản ghi B.SDT) AND [(Bản ghi A.CCCD/CMND = Bản ghi B.CCCD/CMND)  OR  (Bản ghi A.MST = Bản ghi B.MST)]" />
            </div>
          </div>

          <div>
            <div
              className="form-block d-none-rq form-benefit"
            >
              <label className="ant-form-item-required" >Mô tả:</label>
              <TextArea rows={2} value={stData.desc} onChange={(e) => stChange({ desc: e.target.value })} placeholder="(Quy tắc 1.2: Trùng khớp giấy tờ định danh pháp lý + SDT" />
            </div>
          </div>

          <div className="fz-16 fw-500 text-left justify-start mb-12px">Thiết lập điều kiện :</div>

          {/* Render first group (if any) above buttons */}
          {groups.length > 0 && <div>{groups[0] && <GroupCard group={groups[0]} />}</div>}

          {/* Buttons cluster */}
          <div className="mb-12-gap-8" style={{ display: "flex", alignItems: "center" }}>
            <Button className='btn btn-primary-none-bg' onClick={() => addGroup("AND")} disabled={hasType("AND")} block icon={<PlusOutlined />}>
              Thêm nhóm AND
            </Button>
            {/* <Button className='btn btn-primary-none-bg' onClick={() => addGroup("OR")} disabled={hasType("OR")} block icon={<PlusOutlined />}>
              Thêm nhóm OR
            </Button> */}
            <div style={{ marginLeft: "auto", color: "#8c8c8c" }} />
          </div>

          {/* Render second group (if any) below buttons */}
          {groups.length > 1 && <div>{groups[1] && <GroupCard stData={stData} group={groups[1]} />}</div>}
        </div>
      </div>
    </div>
  );
}
